/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { globalRef, LogLevel, loggerRef, type LogEvent, type Unsubscribe } from '@amazon/vinyl-util'
import { redactText } from '@/redactSensitive'

export const LOG_CHANNEL_NAME = 'bf-log'
export const MAX_LOG_LINES = 500
const MAX_LINE_CHARS = 2000

export interface LogLine {
    readonly time: number
    readonly level: string
    readonly prefix: string
    readonly text: string
}

/** Messages the app posts to log windows. */
export type LogStreamMessage =
    | { readonly type: 'snapshot'; readonly lines: readonly LogLine[] }
    | { readonly type: 'line'; readonly line: LogLine }

const streamRef = globalRef(() => ({
    lines: [] as LogLine[],
    listeners: new Set<(line: LogLine) => void>(),
    channel: undefined as BroadcastChannel | undefined,
}))

function formatMessage(message: unknown): string {
    if (typeof message === 'string') return message
    if (message instanceof Error) return message.stack || message.message
    try {
        return (JSON.stringify(message) as string | undefined) ?? String(message)
    } catch {
        return String(message)
    }
}

export function toLogLine(event: LogEvent, time: number = Date.now()): LogLine {
    const text = redactText(event.messages.map(formatMessage).join(' '))
    return {
        time,
        level: LogLevel[event.level].toLowerCase(),
        prefix: event.target.logPrefix,
        text: text.length > MAX_LINE_CHARS ? `${text.slice(0, MAX_LINE_CHARS)}...` : text,
    }
}

function record(event: LogEvent): void {
    const { lines, listeners, channel } = streamRef.value
    const line = toLogLine(event)
    lines.push(line)
    if (lines.length > MAX_LOG_LINES) lines.shift()
    for (const listener of listeners) listener(line)
    channel?.postMessage({ type: 'line', line } satisfies LogStreamMessage)
}

/** The most recent {@link MAX_LOG_LINES} entries, oldest first. */
export function getLogLines(): readonly LogLine[] {
    return streamRef.value.lines
}

export function onLogLine(listener: (line: LogLine) => void): Unsubscribe {
    const { listeners } = streamRef.value
    listeners.add(listener)
    return () => {
        listeners.delete(listener)
    }
}

/** Records every Vinyl logger event and serves them to log windows. Returns the stop function. */
export function startLogStream(): Unsubscribe {
    const stopLogger = loggerRef.value.on('log', record)
    const state = streamRef.value
    const channel = new BroadcastChannel(LOG_CHANNEL_NAME)
    state.channel = channel
    channel.onmessage = (event: MessageEvent<{ type?: string } | null>) => {
        if (event.data?.type !== 'hello') return
        channel.postMessage({ type: 'snapshot', lines: state.lines } satisfies LogStreamMessage)
    }
    return () => {
        stopLogger()
        channel.close()
        state.channel = undefined
    }
}
